import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  closestCenter,
  DndContext,
  DragOverlay,
  MeasuringStrategy,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  useDestroyCompanyTradeMutation,
  useSortCompanyTradesMutation,
} from 'components/hooks/companyTradeMutations';
import useSortSensors from 'components/hooks/useSortSensors';
import useWindowStorage from 'components/hooks/useWindowStorage';

import DestroyModal from 'components/application/DestroyModal';
import SuccessToast from 'components/application/SuccessToast';

import buildCategoryColumns from 'components/contentRegister/components/categoryColumns';
import CategoryRiskAssessmentsPanel from 'components/contentRegister/components/CategoryRiskAssessmentsPanel';
import DragPreviewTable from 'components/contentRegister/components/sortableTable/components/DragPreviewTable';
import GhostSlots from 'components/contentRegister/components/sortableTable/components/GhostSlots';
import SortableRowGroup from 'components/contentRegister/components/sortableTable/components/SortableRowGroup';
import {
  measureRow,
  moveId,
  uniformSlots,
} from 'components/contentRegister/components/sortableTable/helpers/sortables';
import useOrderedIds from 'components/contentRegister/components/sortableTable/hooks/useOrderedIds';
import useOrderedIdsByKey from 'components/contentRegister/components/sortableTable/hooks/useOrderedIdsByKey';
import { useContentRegisterContext } from 'components/contentRegister/contexts/ContentRegisterContext';
import {
  categoryRowElementId,
  returnStateKey,
} from 'components/contentRegister/helpers/returnState';
import { useBreadBoard } from 'components/contexts/Toaster';

import { resourceShape } from 'components/helpers/serialisableResources';

const categoryCellClassName =
  'tw-h-12 tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100';

export default function CategoriesTable({
  domainContentRegister,
  onCategoryDestroy,
  onMasterActivityToggle,
  selectedMasterActivityIds,
}) {
  const { addInedibleToast, addToast, handleBurnToast } = useBreadBoard();
  const { riskRegisterPolicy } = useContentRegisterContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'contentRegister.components.CategoriesTable',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const [getReturnState, , removeReturnState] = useWindowStorage(
    returnStateKey,
    { store: window.sessionStorage },
  );

  const [returnState, setReturnState] = useState(getReturnState);

  const [expanded, setExpanded] = useState(() =>
    returnState?.tradeId ? { [returnState.tradeId]: true } : {},
  );

  const [selectedTradeIdToDelete, setSelectedTradeIdToDelete] = useState(null);
  const [dragLayoutOptions, setDragLayoutOptions] = useState(null);

  const destroyCompanyTradeMutation = useDestroyCompanyTradeMutation();
  const sortCompanyTradesMutation = useSortCompanyTradesMutation();

  const sensors = useSortSensors();

  useEffect(() => {
    removeReturnState();
  }, []);

  const [orderedTradeIds, setOrderedTradeIds] = useOrderedIds(
    domainContentRegister.data.relationships.trades.data.map(
      (trade) => trade.id,
    ),
  );

  const [getOrderedMasterActivityTradeIds, setOrderedMasterActivityTradeIds] =
    useOrderedIdsByKey();

  const columns = useMemo(
    () => buildCategoryColumns({ riskRegisterPolicy }),
    [riskRegisterPolicy],
  );

  const data = useMemo(
    () =>
      orderedTradeIds
        .map((tradeId) => domainContentRegister.included.trade[tradeId])
        .filter(Boolean),
    [domainContentRegister.included.trade, orderedTradeIds],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getRowCanExpand: () => true,
    getRowId: (trade) => trade.id,
    onExpandedChange: setExpanded,
    state: { expanded },
    meta: {
      included: domainContentRegister.included,
      onDeleteClick: setSelectedTradeIdToDelete,
      translations: {
        category: i18nTCommon('shared.category'),
        deleteCategory: (name) =>
          i18nTCommon('shared.deleteName', {
            interpolation: { escapeValue: false },
            name,
          }),
        draft: i18nTCommon('shared.draft'),
        editCategory: i18nT('editCategory'),
        methodStatements: i18nT('methodStatements'),
        noAccessToAllDivisionsUsingCategory: i18nTCommon(
          'riskRegister.noAccessToAllDivisionsUsingCategory',
        ),
        reorderName: (name) =>
          i18nTCommon('shared.reorderName', {
            interpolation: { escapeValue: false },
            name,
          }),
        sequences: i18nT('sequences'),
        status: i18nTCommon('shared.status'),
      },
    },
  });

  const skipExpansionAnimationRef = useRef(false);

  const tableRef = useRef(null);

  // restrictToParentElement can't be used here: each category row is wrapped in its own
  // tbody (to keep it adjacent to its expanded panel), so the row's DOM parent is sized to
  // just that one row rather than the whole table. Clamp to the table wrapper instead.
  const restrictToTable = ({ draggingNodeRect, transform }) => {
    const containerRect = tableRef.current?.getBoundingClientRect();
    if (!draggingNodeRect || !containerRect) return transform;

    const minY = containerRect.top - draggingNodeRect.top;
    const maxY = containerRect.bottom - draggingNodeRect.bottom;

    return { ...transform, y: Math.min(Math.max(transform.y, minY), maxY) };
  };

  const calculateDragLayoutOptions = ({ active }) => {
    const { elementId } = active.data.current;
    const wrapper = tableRef.current;
    const draggedRowMeasurements = measureRow(elementId);

    const firstRowMeasurements = measureRow(
      categoryRowElementId(orderedTradeIds[0]),
    );

    if (!wrapper || !draggedRowMeasurements || !firstRowMeasurements) {
      return null;
    }

    const wrapperTop = wrapper.getBoundingClientRect().top;
    const slotsTop = firstRowMeasurements.top - wrapperTop;
    const draggedIndex = orderedTradeIds.indexOf(active.id);

    const spacerHeight = Math.max(
      0,
      draggedRowMeasurements.top -
        wrapperTop -
        (slotsTop + draggedIndex * draggedRowMeasurements.height),
    );

    return {
      columnWidths: draggedRowMeasurements.columnWidths,
      // Held at its open height so that the page below does not reflow mid-drag.
      frozenHeight: wrapper.offsetHeight,
      slots: uniformSlots({
        count: orderedTradeIds.length,
        height: draggedRowMeasurements.height,
        top: slotsTop + spacerHeight,
      }),
      spacerHeight,
      tradeId: active.id,
      width: draggedRowMeasurements.width,
    };
  };

  const handleDragStart = ({ active }) => {
    const newLayoutOptions = calculateDragLayoutOptions({ active });
    if (!newLayoutOptions) return;

    const hasExpandedRows = Object.values(expanded).some(Boolean);

    setDragLayoutOptions({
      ...newLayoutOptions,
      expandedBeforeDrag: hasExpandedRows ? expanded : null,
    });

    if (hasExpandedRows) setExpanded({});
  };

  const finishDrag = () => {
    const expandedBeforeDrag = dragLayoutOptions?.expandedBeforeDrag;

    skipExpansionAnimationRef.current = Boolean(expandedBeforeDrag);
    setDragLayoutOptions(null);

    if (expandedBeforeDrag) setExpanded(expandedBeforeDrag);
  };

  const handleDragEnd = ({ active, over }) => {
    finishDrag();

    if (!over) return;

    const previousTradeIds = orderedTradeIds;

    const tradeIds = moveId(previousTradeIds, active.id, over.id);

    if (tradeIds === previousTradeIds) return;

    setOrderedTradeIds(tradeIds);

    sortCompanyTradesMutation.mutate(
      {
        companyTradeIds: tradeIds.map(
          (tradeId) =>
            domainContentRegister.included.trade[tradeId].relationships
              .contextualCompanyTrade.data.id,
        ),
      },
      {
        onError: () => {
          setOrderedTradeIds(previousTradeIds);
          addInedibleToast();
        },
      },
    );
  };

  const handleDestroy = () => {
    const { name } = selectedTradeToDelete.attributes;

    destroyCompanyTradeMutation.mutate(
      {
        companyTradeId:
          selectedTradeToDelete.relationships.contextualCompanyTrade.data.id,
      },
      {
        onSuccess: ({ meta }) => {
          if (meta.isContentRegisterAvailable) {
            onCategoryDestroy();

            addToast(
              <SuccessToast
                message={i18nT('deleteSucceeded', {
                  interpolation: { escapeValue: false },
                  name,
                })}
                onBurnToast={handleBurnToast}
              />,
            );
          } else {
            window.location = '/';
          }
        },
        onError: () => addInedibleToast(),
        onSettled: () => setSelectedTradeIdToDelete(null),
      },
    );
  };

  const selectedTradeToDelete =
    domainContentRegister.included.trade[selectedTradeIdToDelete];

  return (
    <>
      <DndContext
        collisionDetection={closestCenter}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        modifiers={[restrictToTable, restrictToVerticalAxis]}
        onDragCancel={finishDrag}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        sensors={sensors}
      >
        <div
          className='tw-relative tw-isolate'
          ref={tableRef}
          style={
            dragLayoutOptions ?
              { height: dragLayoutOptions.frozenHeight }
            : undefined
          }
        >
          {dragLayoutOptions && <GhostSlots slots={dragLayoutOptions.slots} />}
          {/* Border-separate so that a dragged group's transform carries its cells with it, which border-collapse does not survive. */}
          <table
            className='tw-w-full tw-table-fixed tw-border-separate tw-border-spacing-0 tw-border-1 tw-border-b-0 tw-border-solid tw-border-grey-100'
            data-element-name='risk-register-table'
          >
            <thead>
              <tr className='tw-h-10 tw-bg-grey-050'>
                {table.getFlatHeaders().map((header) => (
                  <th
                    className={classNames(
                      'tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900',
                      header.column.columnDef.meta?.classNames,
                    )}
                    key={header.id}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            {dragLayoutOptions?.spacerHeight > 0 && (
              <tbody aria-hidden='true'>
                <tr style={{ height: dragLayoutOptions.spacerHeight }}>
                  <td colSpan={columns.length} />
                </tr>
              </tbody>
            )}
            <SortableContext
              items={data.map((trade) => trade.id)}
              strategy={verticalListSortingStrategy}
            >
              {table.getRowModel().rows.map((row) => {
                const trade = row.original;

                const {
                  id: tradeId,
                  relationships: {
                    masterActivityTrades: { data: masterActivityTradesData },
                  },
                } = trade;

                return (
                  <SortableRowGroup
                    aria-expanded={row.getIsExpanded()}
                    data-element-name='category-row'
                    elementId={categoryRowElementId(tradeId)}
                    expandedRow={
                      row.getIsExpanded() && (
                        <tr>
                          <CategoryRiskAssessmentsPanel
                            columnCount={columns.length}
                            domainContentRegister={domainContentRegister}
                            isImmediateExpansionEnabled={
                              skipExpansionAnimationRef.current
                            }
                            onMasterActivityToggle={onMasterActivityToggle}
                            onReorderMasterActivityTrades={(
                              newMasterActivityTradeIds,
                            ) =>
                              setOrderedMasterActivityTradeIds(
                                tradeId,
                                newMasterActivityTradeIds,
                              )
                            }
                            onScrolledToReturnRow={() => setReturnState(null)}
                            orderedMasterActivityTradeIds={getOrderedMasterActivityTradeIds(
                              tradeId,
                              masterActivityTradesData.map(
                                (masterActivityTrade) => masterActivityTrade.id,
                              ),
                            )}
                            scrollToMasterActivityTradeId={
                              returnState?.tradeId === tradeId ?
                                returnState.masterActivityTradeId
                              : undefined
                            }
                            selectedMasterActivityIds={
                              selectedMasterActivityIds
                            }
                            trade={trade}
                          />
                        </tr>
                      )
                    }
                    key={row.id}
                    onClick={() => {
                      skipExpansionAnimationRef.current = false;
                      row.getToggleExpandedHandler()();
                    }}
                    rowClassName='tw-group tw-cursor-pointer tw-bg-white hover:tw-bg-grey-025'
                    sortableId={tradeId}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        className={classNames(
                          categoryCellClassName,
                          cell.column.columnDef.meta?.classNames,
                        )}
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </SortableRowGroup>
                );
              })}
            </SortableContext>
          </table>
        </div>
        <DragOverlay dropAnimation={null}>
          {dragLayoutOptions &&
            (() => {
              const previewRow = table
                .getRowModel()
                .rows.find((row) => row.id === dragLayoutOptions.tradeId);

              return (
                previewRow && (
                  <DragPreviewTable
                    cellClassName={categoryCellClassName}
                    columns={table.getFlatHeaders().map((header, index) => ({
                      id: header.id,
                      width: dragLayoutOptions.columnWidths?.[index],
                    }))}
                    row={previewRow}
                    width={dragLayoutOptions.width}
                  />
                )
              );
            })()}
        </DragOverlay>
      </DndContext>
      {selectedTradeIdToDelete && (
        <DestroyModal
          confirmationText={i18nT('deleteCategory')}
          displayText={i18nTCommon('shared.areYouSureYouWantToDelete', {
            interpolation: { escapeValue: false },
            name: selectedTradeToDelete.attributes.name,
          })}
          isOpen={true}
          onClose={() => setSelectedTradeIdToDelete(null)}
          onDestroy={handleDestroy}
        />
      )}
    </>
  );
}

CategoriesTable.propTypes = {
  domainContentRegister: PropTypes.shape({
    data: resourceShape('contentRegister').isRequired,
    included: PropTypes.shape({
      company: PropTypes.objectOf(resourceShape('company')).isRequired,
      masterActivity: PropTypes.objectOf(resourceShape('masterActivity'))
        .isRequired,
      masterActivityTrade: PropTypes.objectOf(
        resourceShape('masterActivityTrade'),
      ).isRequired,
      personnel: PropTypes.objectOf(resourceShape('personnel')).isRequired,
      profilePhoto: PropTypes.objectOf(resourceShape('profilePhoto'))
        .isRequired,
      trade: PropTypes.objectOf(resourceShape('trade')).isRequired,
      user: PropTypes.objectOf(resourceShape('user')).isRequired,
    }).isRequired,
  }).isRequired,
  onCategoryDestroy: PropTypes.func.isRequired,
  onMasterActivityToggle: PropTypes.func.isRequired,
  selectedMasterActivityIds: PropTypes.instanceOf(Set).isRequired,
};
